import type { PaginatedResult } from '../types/api';
import { getAccessToken } from '../auth/tokenStorage';

import { expireSession, refreshAccessToken } from './refreshAccessToken';
import { unwrapNestedData } from './unwrap';

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000';

export class ApiError extends Error {
    status: number;
    body?: unknown;

    constructor(message: string, status: number, body?: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

export async function apiRequest<T>(
    path: string,
    options: RequestInit = {},
    retry = true,
): Promise<T> {
    const headers = new Headers(options.headers);
    /** FormData must keep the browser-generated multipart boundary. */
    const isFormData = options.body instanceof FormData;
    if (!headers.has('Content-Type') && options.body && !isFormData) {
        headers.set('Content-Type', 'application/json');
    }
    const token = getAccessToken();
    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    const res = await fetch(`${baseUrl}${path}`, { ...options, headers });

    if (res.status === 401) {
        if (retry) {
            const newToken = await refreshAccessToken();
            if (newToken) {
                return apiRequest<T>(path, options, false);
            }
        }
        expireSession();
        throw new ApiError('Unauthorized', 401);
    }

    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : null;

    if (!res.ok) {
        const message =
            (json as { message?: string })?.message ??
            (Array.isArray((json as { message?: string[] })?.message)
                ? (json as { message: string[] }).message.join(', ')
                : res.statusText);
        throw new ApiError(message, res.status, json);
    }

    return unwrapNestedData<T>(json);
}

export async function apiList<T>(
    path: string,
    params: Record<string, string | number | undefined>,
): Promise<PaginatedResult<T>> {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
            search.set(key, String(value));
        }
    });
    const qs = search.toString();
    const result = await apiRequest<PaginatedResult<T>>(qs ? `${path}?${qs}` : path);
    if (Array.isArray(result)) {
        return { data: result, total: result.length };
    }
    return result;
}

export async function apiPublicRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers = new Headers(options.headers);
    if (!headers.has('Content-Type') && options.body) {
        headers.set('Content-Type', 'application/json');
    }
    const res = await fetch(`${baseUrl}${path}`, { ...options, headers });
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : null;
    if (!res.ok) {
        throw new ApiError(res.statusText, res.status, json);
    }
    return unwrapNestedData<T>(json);
}
